import { useEffect, useRef, useState } from "react";
import { ArrowUp, ArrowDown } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Twee floating 3D-buttons rechtsonder: scroll-naar-boven / scroll-naar-beneden.
 *
 * R53 - Scroll-activity visibility:
 * - Default volledig onzichtbaar (opacity-0 + pointer-events-none).
 * - Bij scroll-event op window: fade-in (150ms) en zichtbaar.
 * - 1000ms na laatste scroll-event: fade-out (300ms).
 * - Hover op de FAB-container houdt hem zichtbaar tot mouse-leave + 1s.
 * - Smooth-scroll triggert zelf scroll-events => geen flicker.
 *
 * Scroll-element: window (R52 gebruikt `useWindowVirtualizer`, dus de page-scroll
 * blijft op window, niet op een interne container).
 */
const ScrollHelpers = () => {
  const [showUp, setShowUp] = useState(false);
  const [showDown, setShowDown] = useState(false);
  const [active, setActive] = useState(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoveringRef = useRef(false);

  // Bereken of er iets te scrollen valt en in welke richting (knoppen mogen
  // alleen tonen als de richting zinvol is).
  useEffect(() => {
    const update = () => {
      const scrollY = window.scrollY;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const scrollable = max > 100;
      setShowUp(scrollable && scrollY > 200);
      setShowDown(scrollable && scrollY < max - 50);
    };
    update();
    const obs = new MutationObserver(update);
    obs.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("resize", update);
    return () => {
      obs.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  // Scroll-activity tracking + debounce hide.
  useEffect(() => {
    const scheduleHide = () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      hideTimerRef.current = setTimeout(() => {
        if (!hoveringRef.current) setActive(false);
      }, 1000);
    };

    const onScroll = () => {
      // Update richting-zichtbaarheid live tijdens scroll
      const scrollY = window.scrollY;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const scrollable = max > 100;
      setShowUp(scrollable && scrollY > 200);
      setShowDown(scrollable && scrollY < max - 50);

      setActive(true);
      scheduleHide();
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  const onMouseEnter = () => {
    hoveringRef.current = true;
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    setActive(true);
  };

  const onMouseLeave = () => {
    hoveringRef.current = false;
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setActive(false), 1000);
  };

  // R54: scroll naar absolute top — window-scroller (useWindowVirtualizer).
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // R54: scroll naar absolute bottom met retry-loop.
  // Reden: tijdens smooth-scroll meet de virtualizer nieuwe (mogelijk grotere) rij-
  // hoogtes via measureElement, waardoor `document.documentElement.scrollHeight`
  // GROEIT onderweg. De oorspronkelijke target raakt achterhaald → animatie stopt
  // halverwege. We polleren tot scrollHeight stabiel is en re-targeten opnieuw.
  const scrollToBottom = () => {
    let lastMax = -1;
    let stableTicks = 0;
    let attempts = 0;
    const maxAttempts = 60; // ~3s @ 50ms

    const tick = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const atBottom = window.scrollY >= max - 2;

      // Re-target wanneer de bottom verschoven is of we nog niet beneden zijn.
      if (max !== lastMax) {
        window.scrollTo({ top: max, behavior: "smooth" });
        lastMax = max;
        stableTicks = 0;
      } else if (atBottom) {
        stableTicks++;
      }

      attempts++;
      // Stop wanneer scrollHeight 3 ticks stabiel is EN we onderaan zitten,
      // of bij timeout.
      if ((stableTicks >= 3 && atBottom) || attempts >= maxAttempts) return;
      setTimeout(tick, 50);
    };
    tick();
  };

  const fab =
    "relative inline-flex items-center justify-center h-12 w-12 rounded-full " +
    "bg-gradient-to-b from-slate-700 to-slate-900 text-white ring-1 ring-slate-500/40 " +
    "shadow-[0_4px_10px_rgba(0,0,0,0.30),inset_0_1px_0_rgba(255,255,255,0.35),inset_0_-1px_0_rgba(0,0,0,0.25)] " +
    "hover:-translate-y-0.5 active:translate-y-0";

  // Snelle fade-in (150ms), iets langzamere fade-out (300ms).
  const fadeBase = active
    ? "opacity-100 transition-opacity duration-150"
    : "opacity-0 transition-opacity duration-300";

  return (
    <div
      className="fixed bottom-24 right-6 z-40 flex flex-col gap-2 pointer-events-none"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <button
        type="button"
        aria-label="Scroll naar boven"
        onClick={scrollToTop}
        className={cn(
          fab,
          fadeBase,
          active && showUp ? "pointer-events-auto" : "pointer-events-none",
          !showUp && "opacity-0 pointer-events-none",
        )}
      >
        <span className="pointer-events-none absolute inset-x-1 top-0.5 h-1/2 rounded-full bg-white/20 blur-[1px]" />
        <ArrowUp className="relative h-5 w-5" />
      </button>
      <button
        type="button"
        aria-label="Scroll naar beneden"
        onClick={scrollToBottom}
        className={cn(
          fab,
          fadeBase,
          active && showDown ? "pointer-events-auto" : "pointer-events-none",
          !showDown && "opacity-0 pointer-events-none",
        )}
      >
        <span className="pointer-events-none absolute inset-x-1 top-0.5 h-1/2 rounded-full bg-white/20 blur-[1px]" />
        <ArrowDown className="relative h-5 w-5" />
      </button>
    </div>
  );
};

export default ScrollHelpers;
